import { useCallback, useMemo } from 'react'
import { DateTime } from 'luxon'
import ms from 'ms.macro'
import { useCountdownWhen } from 'utils/hooks/useCountdownWhen'
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useUser } from 'hooks/user/useUser'
import { currencyFormatter } from 'utils'
import { isAddressesEqual } from 'utils/wallet/addresses'
import { G_BUSTED_BID_STATUS, G_BUSTED_SOCKET_CHANNELS, G_BUSTED_STATES } from 'constants/games/busted'
import { useSocketEmitter, useSocketListener } from 'hooks/sockets/useSocket'

const INIT_Y_MAX = 2
const INIT_X_MAX = 2 * 1000
const WAIT_COUNTER_INIT = 4

const BUSTED_INIT_STATE = {
  state: G_BUSTED_STATES.INIT,
  startTs: DateTime.utc().toMillis(),
  roundId: 0,
  dataArr: [{ x: 0, y: 1 }],
  betCounter: WAIT_COUNTER_INIT,
}

const bustedBidListAtom = atom([])
const updateBustedListAtom = atom(null, (get, set, newBid) => {
  set(bustedBidListAtom, (prev) => {
    const filteredBids = prev.filter((bid) => !isAddressesEqual(bid.address, newBid.address))
    return [newBid, ...filteredBids]
  })
})

const bustedGameStateAtom = atom(BUSTED_INIT_STATE)
const updateBustedGameStateAtom = atom(null, (get, set, newState) => {
  set(bustedGameStateAtom, (prev) => ({
    ...prev,
    ...newState,
  }))
})

export const useBustGameLogic = () => {
  const { state, startTs, dataArr } = useAtomValue(bustedGameStateAtom)
  const updateGameStatus = useSetAtom(updateBustedGameStateAtom)
  const updateBustedBidList = useSetAtom(updateBustedListAtom)
  const resetBustedBidList = useSetAtom(bustedBidListAtom)

  useSocketListener('bustabit', G_BUSTED_SOCKET_CHANNELS.WAIT, (msg) => {
    updateGameStatus({
      ...BUSTED_INIT_STATE,
      state: G_BUSTED_STATES.WAIT,
      roundId: msg.next_round_id,
    })
    resetBustedBidList([])
  })

  useSocketListener('bustabit', G_BUSTED_SOCKET_CHANNELS.START, (msg) => {
    updateGameStatus({
      ...BUSTED_INIT_STATE,
      state: G_BUSTED_STATES.START,
      startTs: DateTime.utc().toMillis(),
      roundId: msg.round_info.round_id,
    })
  })

  useSocketListener('bustabit', G_BUSTED_SOCKET_CHANNELS.CRASH, (msg) => {
    const { round_info, crashMultiplier } = msg
    const newX = DateTime.utc().toMillis() - startTs
    const newY = Number(crashMultiplier.toFixed(2))
    updateGameStatus({
      state: G_BUSTED_STATES.CRASH,
      roundId: round_info.round_id,
      dataArr: [...dataArr, { x: newX, y: newY }],
    })
  })

  useSocketListener('bustabit', G_BUSTED_SOCKET_CHANNELS.GAME_STATUS, (msg) => {
    const { currentMultiplier, round_id } = msg
    const newX = DateTime.utc().toMillis() - startTs
    const newY = Number(currentMultiplier.toFixed(2))
    updateGameStatus({ round_id, dataArr: [...dataArr, { x: newX, y: newY }] })
  })

  useSocketListener('bustabit', G_BUSTED_SOCKET_CHANNELS.BIDS, (msg) => {
    updateBustedBidList(msg)
  })

  useCountdownWhen(
    (count) => {
      updateGameStatus({ betCounter: count < 0 ? 0 : count })
    },
    WAIT_COUNTER_INIT,
    ms`0.01s`,
    0.01,
    state === G_BUSTED_STATES.WAIT,
    true
  )
}

export const useBustedGameData = () => {
  const { state, roundId, dataArr, betCounter } = useAtomValue(bustedGameStateAtom)

  // calculate largest piece of data
  let arrayLen = dataArr.length
  let largestY = 0
  for (var hy = 0; hy < arrayLen; hy++) if (dataArr[hy].y > largestY) largestY = dataArr[hy].y
  largestY = largestY < INIT_Y_MAX ? INIT_Y_MAX : largestY
  let initialX = INIT_X_MAX

  let highestX = 0
  for (var hx = 0; hx < arrayLen; hx++) if (dataArr[hx].x > highestX) highestX = dataArr[hx].x
  highestX = highestX < initialX ? initialX : highestX

  const currentMultiplier = currencyFormatter(dataArr.at(-1).y, { noCurrencySign: true })

  return { state, roundId, dataArr, currentMultiplier, betCounter, initialX, largestY, highestX, arrayLen }
}

export const useBustedGameActions = () => {
  const { emit: bustabitEmit } = useSocketEmitter('bustabit')
  const { roundId, dataArr } = useBustedGameData()
  const { account } = useUser('wallet')
  const placeBid = useCallback(
    (amount) => {
      const bidInfo = { user_id: account, round_id: roundId, bet_amount: amount }
      bustabitEmit(G_BUSTED_SOCKET_CHANNELS.BET, bidInfo)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roundId, account]
  )
  const cashoutBid = useCallback(() => {
    bustabitEmit(G_BUSTED_SOCKET_CHANNELS.CASHOUT, {
      user_id: account,
      round_id: roundId,
      cashout_multiplier: dataArr.at(-1).y.toFixed(2),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundId, dataArr])

  return { placeBid, cashoutBid }
}

export const useBustBidList = () => {
  const { account } = useUser('wallet')
  const [bidList, setBidList] = useAtom(bustedBidListAtom)

  const myLiveBid = useMemo(
    () =>
      bidList.filter((bid) => {
        return isAddressesEqual(bid.address, account) && bid.status === G_BUSTED_BID_STATUS.LIVE
      }),
    [bidList, account]
  )

  return { bidList, myLiveBid }
}
